import {VariantProps} from 'cva';
import React from 'react';
import {cva, styles} from '../../Utilities/defineStyles';
import {FieldLabel, FieldLabelWithInfo} from '../_internal/FieldLabel/FieldLabel';
import {RsnRadioButton} from '../RsnRadioButton/RsnRadioButton';

interface RadioGroupOption<T> {
    label: string;
    value: T;
    key?: string;
}

type InputAttributes = Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    keyof React.HTMLAttributes<never> | 'onChange' | 'value'
>;

interface RadioGroupProps<T> extends InputAttributes, VariantProps<typeof cls.radioGroup> {
    label: React.ReactNode;
    options: RadioGroupOption<NoInfer<T>>[];
    value: T;
    onChange: (value: T) => void;
}

export function RsnRadioGroup<T = string>({
    label,
    options,
    value,
    onChange,
    name,
    layout,
    disabled,
    ...inputAttributes
}: RadioGroupProps<T>) {
    if (typeof label === 'string') {
        label = <RsnRadioGroup.Label>{label}</RsnRadioGroup.Label>;
    }
    if (options.length === 1) {
        disabled = true;
    }
    if (options.length === 0) {
        return null;
    }
    return (
        <div className={cls.radioGroupContainer}>
            {label}
            <fieldset className={cls.radioGroup({layout})} disabled={disabled}>
                {options.map(({label, value: optionValue, key}) => (
                    <label
                        className={`${cls.radioLabel} ${disabled ? 'opacity-50' : ''}`}
                        key={key || label}
                        aria-disabled={disabled}
                    >
                        <RsnRadioButton
                            {...inputAttributes}
                            name={name}
                            checked={value === optionValue}
                            onChange={() => onChange(optionValue)}
                        />
                        <span>{label}</span>
                    </label>
                ))}
            </fieldset>
        </div>
    );
}

RsnRadioGroup.Label = FieldLabel.bind(null);
RsnRadioGroup.Label.displayName = 'RsnRadioGroup.Label';

RsnRadioGroup.LabelWithInfo = FieldLabelWithInfo.bind(null);
RsnRadioGroup.LabelWithInfo.displayName = 'RsnRadioGroup.LabelWithInfo';

export namespace RsnRadioGroup {
    export type Props<T> = RadioGroupProps<T>;
    export type Option<T> = RadioGroupOption<T>;
}

const cls = styles({
    radioGroup: cva('gap-2', {
        variants: {
            layout: {
                grid: 'grid grid-cols-2',
                column: 'flex flex-col',
            },
        },
        defaultVariants: {
            layout: 'grid',
        },
    }),
    radioGroupContainer: 'fv-text-m-regular flex flex-col gap-2 text-neutral-100',
    radioLabel: 'fv-text-l-regular flex items-center gap-2 py-2 [fieldset:disabled_&]:text-neutral-60',
});
